import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useContext, useCallback } from 'react';
import { useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { AuthContext, useApiClient, ApiClientService } from 'libs.react_internal';
import { SiteDeleteErrorCode } from '../../components/delete_workspace_sites_modal';
export const useDeleteWorkspaceSites = () => {
    const { entityId } = useContext(AuthContext);
    const queryClient = useQueryClient();
    const momClient = useApiClient(ApiClientService.MOM);
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const deleteSites = useCallback(async (payload) => {
        await momClient.post(`/v1/entities/${entityId}/sites/delete`, payload, {
            no400Handling: true,
        });
    }, [momClient, entityId]);
    const mutation = useMutation({
        mutationFn: deleteSites,
        onSuccess: (_data, payload) => {
            // Invalidate query after successful deletion
            queryClient.invalidateQueries({
                queryKey: ['entities', entityId.toString(), 'sites'],
            });
            // Show success notification
            addNotification({
                title: translate('{count} sites were deleted successfully', { count: payload.siteIds.length }),
                type: 'success',
            });
        },
        onError: (error) => {
            // Don't show toast if:
            // 1. API client already handled the error (errorHandled flag)
            // 2. It's a validation error (INVALID_ARGUMENT_SITE) - handled by ValidationErrorModal
            const errorData = error.response?.data;
            const isValidationError = errorData?.errCode === SiteDeleteErrorCode.INVALID_ARGUMENT_SITE;
            const isAlreadyHandled = errorData?.errorHandled;
            if (isValidationError || isAlreadyHandled) {
                return;
            }
            // Show error notification for unexpected errors
            addNotification({
                title: translate('Failed to delete sites'),
                subtitle: translate(error.message),
                type: 'error',
            });
        },
    });
    return {
        deleteWorkspaceSites: mutation.mutate,
        deleteWorkspaceSitesAsync: mutation.mutateAsync,
        isDeleting: mutation.isPending,
        isError: mutation.isError,
        error: mutation.error,
    };
};
